import { areaAka, areaMatches, type CommunityArea } from "@mm/shared";
import { useQuery } from "@tanstack/react-query";
import { type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes, useId, useMemo, useState } from "react";
import { api, uploadImage, ApiError } from "@/lib/api";

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

type Variant = "primary" | "rose" | "secondary" | "ghost" | "danger";
const variants: Record<Variant, string> = {
  primary: "bg-ink text-white hover:bg-ink-soft",
  rose: "bg-rose text-ink hover:brightness-95",
  secondary: "bg-mist text-ink hover:bg-sage",
  ghost: "bg-white text-ink border border-line hover:bg-mist",
  danger: "bg-white text-danger border border-line hover:bg-blush",
};

export function Button({ variant = "primary", loading, className, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; loading?: boolean }) {
  return (
    <button
      {...rest}
      disabled={rest.disabled || loading}
      className={cx("inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-5 font-semibold transition disabled:opacity-50", variants[variant], className)}
    >
      {loading ? <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden /> : null}
      {children}
    </button>
  );
}

/** Destructive actions need a second tap — no browser confirm() popups on phones. */
export function ConfirmButton({ label, confirmLabel, onConfirm, variant = "danger", className }: { label: string; confirmLabel: string; onConfirm(): Promise<unknown> | void; variant?: Variant; className?: string }) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!armed) return <Button variant={variant} className={className} onClick={() => setArmed(true)}>{label}</Button>;
  return (
    <div className={cx("flex gap-2", className)}>
      <Button
        variant="danger"
        className="flex-1 !bg-danger !text-white"
        loading={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await onConfirm();
          } finally {
            setBusy(false);
            setArmed(false);
          }
        }}
      >
        {confirmLabel}
      </Button>
      <Button variant="ghost" onClick={() => setArmed(false)}>Keep</Button>
    </div>
  );
}

export function Field({ label, hint, error, children }: { label: string; hint?: string | null; error?: string | null; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <div className="mb-4">
      <label htmlFor={id} className="mb-1.5 block text-sm font-semibold">{label}</label>
      {children(id)}
      {error ? <p className="mt-1 text-sm text-danger" role="alert">{error}</p> : hint ? <p className="mt-1 text-sm text-muted">{hint}</p> : null}
    </div>
  );
}

const inputCls = "w-full min-h-12 rounded-xl border-[1.5px] border-line bg-white px-3.5 text-ink outline-none focus:border-ink-soft";
export const Input = (p: InputHTMLAttributes<HTMLInputElement>) => <input {...p} className={cx(inputCls, p.className)} />;
export const Textarea = (p: TextareaHTMLAttributes<HTMLTextAreaElement>) => <textarea rows={4} {...p} className={cx(inputCls, "py-3", p.className)} />;
export const Select = (p: SelectHTMLAttributes<HTMLSelectElement>) => <select {...p} className={cx(inputCls, "appearance-none", p.className)} />;

export function Toggle({ label, hint, checked, onChange }: { label: string; hint?: string; checked: boolean; onChange(v: boolean): void }) {
  return (
    <label className="mb-4 flex min-h-12 cursor-pointer items-center gap-3">
      <span className="flex-1">
        <span className="block font-semibold">{label}</span>
        {hint ? <span className="block text-sm text-muted">{hint}</span> : null}
      </span>
      <input type="checkbox" role="switch" className="peer sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="relative h-7 w-12 shrink-0 rounded-full bg-sage transition after:absolute after:left-1 after:top-1 after:size-5 after:rounded-full after:bg-white after:transition peer-checked:bg-ink peer-checked:after:translate-x-5 peer-focus-visible:outline-3 peer-focus-visible:outline-rose" />
    </label>
  );
}

export function Card({ children, className, onClick }: { children: ReactNode; className?: string; onClick?: () => void }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag onClick={onClick} className={cx("block w-full min-w-0 max-w-full rounded-2xl border border-line bg-white p-4 text-left", onClick && "transition hover:bg-mist active:scale-[0.99]", className)}>
      {children}
    </Tag>
  );
}

export function Badge({ children, tone = "mist" }: { children: ReactNode; tone?: "mist" | "butter" | "blush" | "sage" | "ink" }) {
  const tones = { mist: "bg-mist", butter: "bg-butter", blush: "bg-blush", sage: "bg-sage", ink: "bg-ink text-white" };
  return <span className={cx("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold", tones[tone])}>{children}</span>;
}

export function ErrorText({ error }: { error: unknown }) {
  if (!error) return null;
  return <p className="my-2 rounded-xl bg-blush px-3 py-2 text-sm" role="alert">{error instanceof ApiError ? error.message : "Something went wrong. Please try again."}</p>;
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rounded-2xl border border-dashed border-seafoam p-6 text-center">
      <p className="font-semibold">{title}</p>
      {children ? <div className="mt-1 text-sm text-muted">{children}</div> : null}
    </div>
  );
}

export function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="mb-6">
      <div className="mb-2 flex items-end justify-between gap-2">
        <h2 className="text-lg font-bold">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export type Area = CommunityArea;
export const useAreas = () => useQuery({ queryKey: ["areas"], queryFn: () => api<Area[]>("/v1/community-areas"), staleTime: 86_400_000 });

/** Searchable checklist of community areas — thumb-friendly; selected ones float to the top. */
export function AreaPicker({ value, onChange }: { value: number[]; onChange(ids: number[]): void }) {
  const areas = useAreas().data ?? [];
  const [q, setQ] = useState("");
  const chosen = new Set(value);
  const list = useMemo(
    () => areas.filter((a) => areaMatches(a, q)).sort((a, b) => Number(chosen.has(b.id)) - Number(chosen.has(a.id)) || a.name.localeCompare(b.name)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [areas, q, value],
  );
  const toggle = (id: number) => onChange(chosen.has(id) ? value.filter((v) => v !== id) : [...value, id]);
  return (
    <div className="rounded-2xl border border-line bg-white">
      <div className="flex flex-wrap gap-1.5 border-b border-line p-3">
        {value.length === 0 ? <span className="text-sm text-muted">No areas selected</span> : null}
        {value.map((id) => (
          <button type="button" key={id} onClick={() => toggle(id)} className="rounded-full bg-ink px-3 py-1 text-sm font-semibold text-white" aria-label={`Remove ${areas.find((a) => a.id === id)?.name}`}>
            {areas.find((a) => a.id === id)?.name ?? id} ✕
          </button>
        ))}
      </div>
      <div className="p-3">
        <Input placeholder="Search areas or neighborhoods (e.g. Andersonville)" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search areas" />
      </div>
      <ul className="max-h-72 overflow-y-auto px-1 pb-2">
        {list.map((a) => (
          <li key={a.id}>
            <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-xl px-3 hover:bg-mist">
              <input type="checkbox" className="size-5 accent-[#083534]" checked={chosen.has(a.id)} onChange={() => toggle(a.id)} />
              <span className="min-w-0">
                <span className="block">{a.name}</span>
                {a.neighborhoods.length ? <span className="block truncate text-sm text-muted">{areaAka(a)}</span> : null}
              </span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ImageUpload({ url, onUploaded, onClear }: { url: string | null; onUploaded(id: string, url: string): void; onClear(): void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <div>
      {url ? <img src={url} alt="" className="mb-2 aspect-[16/9] w-full rounded-2xl object-cover" /> : null}
      <div className="flex gap-2">
        <label className={cx("inline-flex min-h-12 flex-1 cursor-pointer items-center justify-center rounded-full bg-mist px-5 font-semibold", busy && "opacity-50")}>
          {busy ? "Uploading…" : url ? "Replace photo" : "Add a photo"}
          <input
            type="file"
            accept="image/*"
            className="sr-only"
            disabled={busy}
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setBusy(true);
              setError(null);
              try {
                const a = await uploadImage(f);
                onUploaded(a.id, a.fullUrl);
              } catch (err) {
                setError(err);
              } finally {
                setBusy(false);
                e.target.value = "";
              }
            }}
          />
        </label>
        {url ? <Button type="button" variant="ghost" onClick={onClear}>Remove</Button> : null}
      </div>
      <ErrorText error={error} />
    </div>
  );
}

export { cx };
